/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Runtime loader for Leaflet + Leaflet.markercluster.
 *
 * Leaflet alone is ~42 KB gzip — far over the embed's 30 KB bundle budget
 * (UX_GUIDELINE §8). So the map dependencies live OUTSIDE the bundle and are
 * fetched only when the map view is actually shown.
 *
 * Assets are self-hosted on the same origin as embed.js (no third-party CDN —
 * keeps the embed free of cross-origin requests on initiative websites,
 * consistent with the EU-data-sovereignty value). The JS is injected into the
 * host document (Leaflet is a UMD global → `window.L`); the CSS is injected
 * into the embed's Shadow Root so styles stay isolated to the widget.
 */

const CSS_FILES = ["leaflet.css", "MarkerCluster.css", "MarkerCluster.Default.css"];
const CSS_MARKER = "data-cb-leaflet-css";

// Module-level singleton: the JS is loaded at most once per page, no matter
// how many embeds or re-renders ask for it.
let jsPromise: Promise<any> | null = null;

function ensureCss(vendorBase: string, target: ParentNode): void {
  // Each Shadow Root needs its own copy (shadow DOM doesn't inherit document
  // styles). Guard against re-injecting into the same root.
  const root = target as unknown as { querySelector(s: string): Element | null };
  if (root.querySelector?.(`link[${CSS_MARKER}]`)) return;
  for (const file of CSS_FILES) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = vendorBase + file;
    link.setAttribute(CSS_MARKER, "");
    target.appendChild(link);
  }
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-cb-src="${src}"]`,
    );
    if (existing) {
      if (existing.dataset.cbLoaded === "true") return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error(`load ${src}`)), {
        once: true,
      });
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.cbSrc = src;
    script.addEventListener(
      "load",
      () => {
        script.dataset.cbLoaded = "true";
        resolve();
      },
      { once: true },
    );
    script.addEventListener("error", () => reject(new Error(`load ${src}`)), {
      once: true,
    });
    document.head.appendChild(script);
  });
}

/**
 * Load Leaflet (+ markercluster) and inject its CSS into `cssTarget`
 * (the embed's Shadow Root). Resolves with the global `L`.
 *
 * @param vendorBase absolute URL ending in `/`, e.g. `https://cleanups.berlin/vendor/leaflet/`
 * @param cssTarget  the Shadow Root (or document) to inject stylesheets into
 */
export function loadLeaflet(vendorBase: string, cssTarget: ParentNode): Promise<any> {
  ensureCss(vendorBase, cssTarget);
  if (!jsPromise) {
    // markercluster augments the global L, so Leaflet must load first.
    jsPromise = loadScript(vendorBase + "leaflet.js")
      .then(() => loadScript(vendorBase + "leaflet.markercluster.js"))
      .then(() => {
        const L = (globalThis as any).L;
        if (!L) throw new Error("Leaflet did not register a global");
        return L;
      })
      .catch((err) => {
        // Reset so a later view switch can retry after a transient failure.
        jsPromise = null;
        throw err;
      });
  }
  return jsPromise;
}
